/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// หน้าต่าง "รายการไฟล์ที่จะอยู่ใน zip" ของหน้าดาวน์โหลดรูป (1 พ.ย. 2569)
//   ซ้าย = โฟลเดอร์ชั้นแรกใน zip (รหัสสินค้า/รหัสแคมเปญ) พร้อมจำนวน · ติ๊กทั้งโฟลเดอร์ได้
//   ขวา = รูปย่อของไฟล์ ติ๊กออกทีละไฟล์ · ป้าย "รูปหลัก" / "AI <ที่มา>" · กรองเฉพาะ AI / ไม่ใช่ AI
//   ไฟล์ที่เอาออกอยู่ใน `excluded` ของหน้าหลัก (ล้างเองเมื่อที่เลือกเปลี่ยน) · ปุ่มดาวน์โหลดเรียกตัวเดียวกับแผงขวา
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';

export const AI_SOURCE_LABEL = {
  AI_GENERATE: 'AI เจน',
  AI_EDIT: 'AI แก้ภาพ',
  BG_REMOVE: 'AI ลบพื้นหลัง',
  AI_CLEAN: 'AI เก็บงาน',
  SKU_STAMP: 'ใส่รหัสสินค้า',
};
export const mb = (bytes) =>
  bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const topFolder = (f) => f.folder.split('/')[0];

function ExportFileReview({
  open,
  onClose,
  list,
  excluded,
  onToggle,
  onSetMany,
  onDownload,
  progress,
}) {
  const [folder, setFolder] = useState(''); // '' = ทุกโฟลเดอร์
  const [kind, setKind] = useState('all'); // all | ai | manual
  const files = list?.files || [];

  const folders = useMemo(() => {
    const map = new Map();
    files.forEach((f) => {
      const k = topFolder(f);
      const cur = map.get(k) || { name: k, total: 0, on: 0, bytes: 0 };
      cur.total += 1;
      cur.bytes += f.size;
      if (!excluded[f.id]) cur.on += 1;
      map.set(k, cur);
    });
    return Array.from(map.values());
  }, [files, excluded]);

  const shown = files.filter(
    (f) =>
      (!folder || topFolder(f) === folder) &&
      (kind === 'all' || (kind === 'ai' ? f.ai === 'Y' : f.ai !== 'Y'))
  );
  const aiTotal = files.filter((f) => f.ai === 'Y').length;
  const picked = files.filter((f) => !excluded[f.id]);
  const pickedBytes = picked.reduce((a, f) => a + f.size, 0);
  const current = folders.find((x) => x.name === folder);

  const folderBtn = (f) => {
    const all = f.on === f.total;
    const none = f.on === 0;
    return (
      <MDBox
        key={f.name}
        display='flex'
        alignItems='center'
        gap={0.5}
        px={0.5}
        sx={{
          borderRadius: 1.5,
          bgcolor: folder === f.name ? '#e8f1fd' : 'transparent',
          '&:hover': { bgcolor: folder === f.name ? '#e8f1fd' : '#f0f2f5' },
        }}
      >
        <Checkbox
          size='small'
          checked={all}
          indeterminate={!all && !none}
          onChange={() =>
            onSetMany(
              files.filter((x) => topFolder(x) === f.name).map((x) => x.id),
              all // ติ๊กอยู่ครบ = เอาออกทั้งโฟลเดอร์ · ไม่งั้นติ๊กให้ครบ
            )
          }
          inputProps={{ 'aria-label': `ติ๊กโฟลเดอร์ ${f.name}` }}
          sx={{ p: 0.5 }}
        />
        <MDBox
          component='button'
          type='button'
          onClick={() => setFolder(folder === f.name ? '' : f.name)}
          aria-pressed={folder === f.name}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            py: 0.75,
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
          }}
        >
          <Icon fontSize='small' sx={{ color: 'text.main' }}>
            folder
          </Icon>
          <MDTypography
            variant='button'
            fontWeight={folder === f.name ? 'bold' : 'regular'}
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {f.name}
          </MDTypography>
          <MDTypography variant='caption' color='text'>
            {f.on === f.total ? f.total : `${f.on}/${f.total}`}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      aria-labelledby='export-review-title'
    >
      <DialogTitle id='export-review-title' sx={{ pb: 1 }}>
        <MDBox
          display='flex'
          alignItems='flex-start'
          justifyContent='space-between'
          gap={2}
          flexWrap='wrap'
        >
          <MDBox>
            <MDTypography variant='h5' fontWeight='bold'>
              รายการไฟล์ที่จะอยู่ใน zip
            </MDTypography>
            <MDTypography variant='caption' color='text' display='block'>
              ติ๊กออกไฟล์ที่ไม่ต้องการได้ · ไฟล์ที่ไม่ติ๊กจะไม่ถูกโหลด ·
              โครงสร้างโฟลเดอร์เหมือนกล่องซ้าย
            </MDTypography>
          </MDBox>
          <MDBox display='flex' alignItems='center' gap={1}>
            <ToggleButtonGroup
              size='small'
              exclusive
              value={kind}
              onChange={(e, v) => v && setKind(v)}
              aria-label='แสดงเฉพาะ'
            >
              <ToggleButton value='all' sx={{ px: 1.5, textTransform: 'none' }}>
                ทั้งหมด {files.length}
              </ToggleButton>
              <ToggleButton value='ai' sx={{ px: 1.5, textTransform: 'none', gap: 0.5 }}>
                <Icon fontSize='small'>auto_awesome</Icon> รูป AI {aiTotal}
              </ToggleButton>
              <ToggleButton value='manual' sx={{ px: 1.5, textTransform: 'none' }}>
                ไม่ใช่ AI {files.length - aiTotal}
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButton aria-label='ปิด' onClick={onClose} size='small'>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </MDBox>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 420 }}>
        <MDBox
          component='nav'
          aria-label='โฟลเดอร์'
          sx={{
            width: 250,
            flex: '0 0 auto',
            borderRight: '1px solid #e9ecef',
            bgcolor: '#fafbfc',
            p: 1,
            overflowY: 'auto',
            maxHeight: '62vh',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <MDTypography
            variant='caption'
            fontWeight='bold'
            color='text'
            sx={{ px: 1, display: 'block', mb: 0.5 }}
          >
            โฟลเดอร์ใน zip · {folders.length}
          </MDTypography>
          <MDBox
            component='button'
            type='button'
            onClick={() => setFolder('')}
            aria-pressed={folder === ''}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              border: 0,
              borderRadius: 1.5,
              bgcolor: folder === '' ? '#e8f1fd' : 'transparent',
              cursor: 'pointer',
              px: 1,
              py: 0.75,
              font: 'inherit',
              color: 'inherit',
              textAlign: 'left',
            }}
          >
            <Icon fontSize='small' sx={{ color: 'text.main' }}>
              folder_copy
            </Icon>
            <MDTypography
              variant='button'
              fontWeight={folder === '' ? 'bold' : 'regular'}
              sx={{ flex: 1 }}
            >
              ทั้งหมด
            </MDTypography>
            <MDTypography variant='caption' color='text'>
              {picked.length === files.length ? files.length : `${picked.length}/${files.length}`}
            </MDTypography>
          </MDBox>
          {folders.map(folderBtn)}
        </MDBox>

        <MDBox sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <MDBox
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
            flexWrap='wrap'
            px={2.5}
            py={1.25}
            sx={{ borderBottom: '1px solid #e9ecef' }}
          >
            <MDTypography variant='button'>
              {folder ? (
                <>
                  <b>{folder}</b> · {current?.total || 0} ไฟล์ · {mb(current?.bytes || 0)}
                </>
              ) : (
                <>ทุกโฟลเดอร์ · {shown.length} ไฟล์</>
              )}
            </MDTypography>
            <MDBox display='flex' gap={0.75}>
              <MDButton
                size='small'
                variant='outlined'
                color='dark'
                disabled={!shown.length}
                onClick={() =>
                  onSetMany(
                    shown.map((f) => f.id),
                    false
                  )
                }
              >
                ติ๊กที่แสดงทั้งหมด
              </MDButton>
              <MDButton
                size='small'
                variant='outlined'
                color='dark'
                disabled={!shown.length}
                onClick={() =>
                  onSetMany(
                    shown.map((f) => f.id),
                    true
                  )
                }
              >
                เอาออกที่แสดงทั้งหมด
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox sx={{ flex: 1, overflowY: 'auto', maxHeight: '56vh', p: 2 }}>
            {!shown.length && (
              <MDTypography variant='button' color='text'>
                ไม่มีไฟล์ในกลุ่มนี้
              </MDTypography>
            )}
            <MDBox
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 1.5,
              }}
            >
              {shown.map((f) => {
                const on = !excluded[f.id];
                return (
                  <MDBox
                    key={f.id}
                    onClick={() => onToggle(f.id)}
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: on ? 'info.main' : '#e9ecef',
                      borderRadius: 2,
                      overflow: 'hidden',
                      opacity: on ? 1 : 0.55,
                      bgcolor: '#fff',
                    }}
                  >
                    <MDBox sx={{ position: 'relative', aspectRatio: '1 / 1', bgcolor: '#f0f2f5' }}>
                      <MDBox
                        component='img'
                        src={f.url}
                        alt=''
                        loading='lazy'
                        decoding='async'
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                      <Checkbox
                        size='small'
                        checked={on}
                        onChange={() => onToggle(f.id)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ 'aria-label': `เลือกไฟล์ ${f.name}` }}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          left: 2,
                          bgcolor: 'rgba(255,255,255,.85)',
                          borderRadius: 1,
                          p: 0.25,
                        }}
                      />
                      {f.main === 'Y' && (
                        <Chip
                          size='small'
                          label='รูปหลัก'
                          sx={{
                            position: 'absolute',
                            bottom: 6,
                            left: 6,
                            height: 20,
                            bgcolor: 'rgba(52,71,103,.85)',
                            color: '#fff',
                            fontSize: 11,
                          }}
                        />
                      )}
                      {f.ai === 'Y' && (
                        <Chip
                          size='small'
                          color='info'
                          icon={<Icon sx={{ fontSize: '12px !important' }}>auto_awesome</Icon>}
                          label={AI_SOURCE_LABEL[f.ai_source] || 'AI'}
                          sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: 11 }}
                        />
                      )}
                    </MDBox>
                    <MDBox px={1} py={0.75}>
                      <MDTypography
                        variant='button'
                        fontWeight='bold'
                        display='block'
                        noWrap
                        title={f.name}
                      >
                        {f.name}
                      </MDTypography>
                      <MDTypography variant='caption' color='text' display='block' noWrap>
                        {f.folder.includes('/') ? `${f.folder} · ` : ''}
                        {mb(f.size)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions
        sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <MDBox>
          <MDTypography variant='button' fontWeight='bold' display='block'>
            เลือกแล้ว {picked.length} จาก {files.length} ไฟล์ · ≈ {mb(pickedBytes)}
          </MDTypography>
          {progress && (
            <MDBox mt={0.5} sx={{ minWidth: 240 }}>
              <MDProgress
                value={Math.round((progress[0] / Math.max(1, progress[1])) * 100)}
                color='info'
              />
            </MDBox>
          )}
        </MDBox>
        <MDBox display='flex' gap={1}>
          <MDButton variant='outlined' color='dark' onClick={onClose}>
            กลับไปแก้ที่เลือก
          </MDButton>
          <MDButton
            variant='gradient'
            color='info'
            disabled={!picked.length || Boolean(progress)}
            onClick={onDownload}
          >
            <Icon>download</Icon>&nbsp;
            {progress
              ? `กำลังโหลด ${progress[0]}/${progress[1]}`
              : `ดาวน์โหลด ${picked.length} ไฟล์`}
          </MDButton>
        </MDBox>
      </DialogActions>
    </Dialog>
  );
}
ExportFileReview.defaultProps = { list: null, progress: null };
ExportFileReview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  list: PropTypes.shape({ files: PropTypes.arrayOf(PropTypes.shape({})) }),
  excluded: PropTypes.shape({}).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSetMany: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  progress: PropTypes.arrayOf(PropTypes.number),
};

export default ExportFileReview;
